import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Inbox, Users, MailCheck, Building2, Target, Radar,
  PenSquare, Scale, KanbanSquare, BookOpen, Award, Settings, ShieldCheck, Newspaper, Send,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandSeparator,
} from "@/components/ui/command";
import { subscribeCommandActions } from "@/components/common/commandContext";

/* A founder command center deserves a command bar. Cmd/Ctrl+K jumps anywhere.
   Kept to navigation + discoverability so there is no cross-page state risk. */

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, keywords: ["home", "overview", "briefing"] },
  { to: "/inbox", label: "Inbox", icon: Inbox, keywords: ["email", "mail", "compose", "triage"] },
  { to: "/contacts", label: "Contacts", icon: Users, keywords: ["people", "correspondents"] },
  { to: "/receipts", label: "Read Receipts", icon: MailCheck, keywords: ["tracking", "opens", "pixel"] },
  { to: "/intelligence", label: "Intelligence", icon: Building2, keywords: ["companies", "workspace", "insights"] },
  { to: "/pipeline", label: "Pipeline", icon: Target, keywords: ["deals", "crm", "stages"] },
  { to: "/signals", label: "Signals", icon: Radar, keywords: ["watchlist", "actions", "monitoring"] },
  { to: "/content", label: "Content", icon: PenSquare, keywords: ["post", "linkedin", "x", "tweet", "studio", "schedule"] },
  { to: "/news", label: "News briefing", icon: Newspaper, keywords: ["stories", "feed", "topics"] },
  { to: "/ledger", label: "Ledger", icon: Scale, keywords: ["commitments", "promises", "asks"] },
  { to: "/projects", label: "Projects", icon: KanbanSquare, keywords: ["tasks", "kanban", "boards"] },
  { to: "/review", label: "Weekly Review", icon: BookOpen, keywords: ["investor update", "column", "recap"] },
  { to: "/scorecard", label: "Scorecard", icon: Award, keywords: ["focus minutes", "streak", "goals"] },
  { to: "/settings", label: "Settings", icon: Settings, keywords: ["gmail", "connect", "account", "oauth", "focus", "keys"] },
  { to: "/admin/access", label: "Access", icon: ShieldCheck, keywords: ["invites", "admin", "approve"], ownerOnly: true },
  { to: "/sentinel", label: "Sentinel", icon: Send, keywords: ["inbox", "insert", "place", "self", "owner"], ownerOnly: true },
];

export const openCommandPalette = () => window.dispatchEvent(new Event("discern:command"));

export const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const [contextActions, setContextActions] = useState([]);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("discern:command", onOpen);
    const unsub = subscribeCommandActions(setContextActions);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("discern:command", onOpen);
      unsub();
    };
  }, []);

  const go = useCallback(
    (to) => {
      setOpen(false);
      navigate(to);
    },
    [navigate]
  );

  const runAction = useCallback((action) => {
    setOpen(false);
    // Defer so the dialog can close before the action opens another surface (dialog/composer).
    setTimeout(() => { try { action.run?.(); } catch (e) { /* no-op */ } }, 0);
  }, []);

  const items = NAV.filter((n) => !n.ownerOnly || user?.is_owner);
  const emailActions = contextActions.filter((a) => a.group === "email");
  const generalActions = contextActions.filter((a) => a.group !== "email");

  const renderAction = (a) => (
    <CommandItem
      key={a.id}
      value={a.label}
      keywords={a.keywords}
      onSelect={() => runAction(a)}
      data-testid={`command-email-${a.id}`}
    >
      {a.icon ? <a.icon className="text-muted-foreground" /> : null}
      <span>{a.label}</span>
    </CommandItem>
  );

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Type a command or jump to a section..." data-testid="command-palette-input" />
      <CommandList>
        <CommandEmpty>No matches. Try a section name.</CommandEmpty>
        {emailActions.length > 0 && (
          <>
            <CommandGroup heading="This email">
              {emailActions.map(renderAction)}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}
        {generalActions.length > 0 && (
          <>
            <CommandGroup heading="Actions">
              {generalActions.map(renderAction)}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}
        <CommandGroup heading="Go to">
          {items.map((n) => (
            <CommandItem
              key={n.to}
              value={n.label}
              keywords={n.keywords}
              onSelect={() => go(n.to)}
              data-testid={`command-nav-${n.to.replace(/\//g, "-").replace(/^-/, "")}`}
            >
              <n.icon className="text-muted-foreground" />
              <span>{n.label}</span>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};
